// Extract ClerkProfileSettings component

import { useClerk } from '@clerk/clerk-react';
import { faLock, faDownload, faMessageBot, faQrcode, faCameraMovie, faCalendar, faBell } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Privacy } from '@playcastdotio/Settings';
import { useState, useEffect, useContext, ComponentType, ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { CafeSchedule } from './widgets/CafeSchedule';
import { Downloads } from './widgets/Downloads';
import { GameClips } from './widgets/GameClips';
import { Notifications } from './widgets/Notifications';
import { Overlays } from './widgets/Overlays';
import { Tools } from './widgets/Tools';
import constants from '@playcastdotio/GlobalConfig';
import { PlaycastContext } from './PlaycastWrapper';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

/**
 * ClerkProfileSettings component handles the configuration and opening of the Clerk user profile.
 * It provides custom UI styling, manages peer connection settings, and configures custom pages
 * like Privacy, Downloads, Tools, Overlays, GameClips, and Café Schedule.
 * Returns an object with the openUserProfileSettings function that can be used by other components.
 */
export const ClerkProfileSettings = () => {
  const context = useContext(PlaycastContext);
  const { openUserProfile } = useClerk();
  const [allowPeerConnections, setInternalAllowPeerConnections] = useState(context.stateStores.iceServerState.allowPeerConnections);

  const setAllowPeerConnections = (allowPeerConnections: boolean) => {
    context.targetFunctions.setAllowPeerConnections(allowPeerConnections);
    setInternalAllowPeerConnections(allowPeerConnections);
  };

  useEffect(() => {
    setInternalAllowPeerConnections(context.stateStores.iceServerState.allowPeerConnections);
  }, [context.stateStores.iceServerState.allowPeerConnections]);

  const handleQrCodeSwitch = async (showStreamQrCode: boolean) => {
    context.targetFunctions.updateShowStreamQrCode(!showStreamQrCode);
    if (showStreamQrCode) {
      context.targetFunctions.overlayDeleteOverlay('qr-code');
    } else {
      try {
        await context.targetFunctions.createQrCodeOverlay();
      } catch (error) {
        console.error('Failed to create QR code overlay:', error);
        // Revert the switch if overlay creation fails
        context.targetFunctions.updateShowStreamQrCode(showStreamQrCode);
      }
    }
  };

  // Helper function to create custom page config with proper typing
  const createCustomPage = <P extends object>(
    url: string,
    label: string,
    icon: IconDefinition,
    ContentComponent: ComponentType<P>,
    props: P,
  ) => {
    return {
      url,
      label,
      mountIcon: (el: any) => {
        const root = createRoot(el);
        root.render(<FontAwesomeIcon icon={icon} className='text-md' data-testid={`${url}-icon`} />);
        el.__root = root;
      },
      unmountIcon: (el: any) => {
        if (el && el.__root) {
          el.__root.unmount();
          delete el.__root;
        }
      },
      mount: (el: any) => {
        const root = createRoot(el);
        root.render(<ContentComponent {...props} />);
        el.__root = root;
      },
      unmount: (el: any) => {
        if (el && el.__root) {
          el.__root.unmount();
          delete el.__root;
        }
      },
    };
  };

  const openUserProfileSettings = () => {
    const hiddenElementsWhenStreaming = {
      profileSectionItem__phoneNumbers: 'blur-md',
      profileSectionItem__emailAddresses: 'blur-md',
      profileSectionItem__connectedAccounts: 'blur-md',
      profileSectionItemList__username: 'blur-md',
      profileSectionItem__profile: 'blur-md',
      profileSectionItem__profileText: 'blur-md',
      profileSectionPrimaryButton: 'invisible',
      menuButton__mfa: 'invisible',
      activeDeviceListItem: 'blur-md',
      headerTitle: 'sensitive-content',
    };

    const electronElements = {
      card: '!shadow-none',
      rootBox: '!shadow-none',
      userPreviewMainIdentifier: 'font-semibold',
      userPreviewSecondaryIdentifier: 'text-gray-500',
    };

    const remoteElements = {
      card: '!shadow-none',
      rootBox: '!shadow-none',
      userPreviewMainIdentifier: 'font-semibold',
      userPreviewSecondaryIdentifier: 'text-gray-500',
    };

    let showElements = context.electronFunctions ? electronElements : remoteElements;
    if (context.stateStores.hostState.status === 'streaming' && context.electronFunctions) {
      showElements = {
        ...showElements,
        ...hiddenElementsWhenStreaming,
      };
    }

    // Define core pages that are always available with proper typing
    const corePages = [
      createCustomPage('privacy', 'Privacy', faLock, Privacy, {
        allowPeerConnections,
        setAllowPeerConnections,
        edition: context.stateStores.hostState.edition ?? 'standard',
      }),
      //createCustomPage('downloads', 'Downloads', faDownload, Downloads, { context }),
    ];

    const standardEditionPages = [
      createCustomPage('tools', 'Tools', faMessageBot, Tools, { context }),
      createCustomPage('gameclips', 'Game Clips', faCameraMovie, GameClips, { context }),
      createCustomPage('overlays', 'Overlays', faQrcode, Overlays, {
        context,
        handleQrCodeSwitch,
      }),
    ];

    const cafeEditionPages = [
      createCustomPage('cafe', 'Café Schedule', faCalendar, CafeSchedule, {}),
      createCustomPage('notifications', 'Notifications', faBell, Notifications, { context }),
    ];

    // Build the final pages array with conditional items
    const customPages = [
      ...corePages,
      // Add cafe page if edition is pavilion
      ...(context.stateStores.hostState.edition !== 'pavilion' ? [standardEditionPages] : [cafeEditionPages]),
    ];

    openUserProfile({
      appearance: {
        elements: {
          ...showElements,
        },
      },
      customPages: customPages.flat(),
    });
  };

  return { openUserProfileSettings };
};
